import type { Finding, ReportSummary, ScanResult } from "@/pages/portal/autoscan/types/customer";

export type LiveScanStatus = "idle" | "starting" | "running" | "completed" | "error";

const LIVE_SCAN_TOKEN_STORAGE_PREFIX = "shadowscan.live-scan.";

export const getPublicScanApiBase = (): string => {
  const fromEnv = import.meta.env.VITE_PUBLIC_SCAN_API_BASE as string | undefined;
  if (fromEnv) return fromEnv;
  if (typeof document !== "undefined") {
    const meta = document.querySelector<HTMLMetaElement>(
      'meta[name="public-scan-api-base"]',
    );
    const value = meta?.content?.trim();
    if (value && value !== "undefined" && !value.includes("%VITE_")) {
      return value;
    }
  }
  return "https://scan-api.shadowscan.com.au";
};

export const normalizeDomain = (value: string) =>
  String(value || "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/.*$/, "")
    .toLowerCase();

const getHashParams = (hash: string) =>
  new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);

const getLocationToken = (search: string, hash: string): string => {
  const hashParams = getHashParams(hash);
  const searchParams = new URLSearchParams(search);
  return (
    hashParams.get("access_token") ||
    hashParams.get("token") ||
    searchParams.get("token") ||
    ""
  ).trim();
};

const getStoredScanToken = (scanId: string | null | undefined): string => {
  if (!scanId || typeof window === "undefined") return "";
  return window.sessionStorage.getItem(`${LIVE_SCAN_TOKEN_STORAGE_PREFIX}${scanId}`) || "";
};

export const persistScanToken = (scanId: string | null | undefined, token: string | null | undefined) => {
  if (!scanId || !token || typeof window === "undefined") return;
  window.sessionStorage.setItem(`${LIVE_SCAN_TOKEN_STORAGE_PREFIX}${scanId}`, token);
};

export const resolveScanToken = (
  scanId: string | null | undefined,
  search: string,
  hash: string,
): string => {
  const token = getLocationToken(search, hash);
  if (token) {
    persistScanToken(scanId, token);
    return token;
  }
  return getStoredScanToken(scanId);
};

export const scrubScanTokenFromUrl = (search: string, hash: string) => {
  if (typeof window === "undefined") return;
  const hasTokenInQuery = new URLSearchParams(search).has("token");
  const hashParams = getHashParams(hash);
  const hasTokenInHash = hashParams.has("access_token") || hashParams.has("token");
  if (!hasTokenInQuery && !hasTokenInHash) return;
  const url = new URL(window.location.href);
  url.searchParams.delete("token");
  url.hash = "";
  window.history.replaceState(null, document.title, `${url.pathname}${url.search}`);
};

export const buildScanAuthHeaders = (
  token: string | null | undefined,
  baseHeaders?: HeadersInit,
): Headers => {
  const headers = new Headers(baseHeaders);
  if (token) headers.set("x-scan-token", token);
  return headers;
};

export const buildLiveScanUrl = (scanId: string, token?: string | null) =>
  `/live-scan/${scanId}${token ? `#access_token=${encodeURIComponent(token)}` : ""}`;

export const buildLiveScanPdfUrl = (scanId: string, domain: string, token?: string | null) =>
  `/live-scan/${scanId}/${encodeURIComponent(domain)}.pdf${token ? `#access_token=${encodeURIComponent(token)}` : ""}`;

// Validate domain format - matches server-side validation
export const isValidDomain = (domain: string): boolean => {
  if (!domain || domain.length > 253) return false;
  // Basic domain regex - allows alphanumeric, hyphens, and dots
  const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return domainRegex.test(domain);
};

// Validate email format - matches server-side validation
export const isValidEmail = (email: string): boolean => {
  if (!email || email.length > 254) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const summaryFromFindings = (findings: Finding[]): ReportSummary => ({
  totalFindings: findings.length,
  critical: findings.filter((f) => f.severity === "critical").length,
  high: findings.filter((f) => f.severity === "high").length,
  medium: findings.filter((f) => f.severity === "medium").length,
  low: findings.filter((f) => f.severity === "low").length,
  info: findings.filter((f) => f.severity === "info").length,
});

export const extractFindings = (payload: unknown): Finding[] => {
  const data = payload as {
    findings?: unknown;
    results?: unknown;
    scanResults?: unknown;
    result?: { results?: unknown };
  };
  if (Array.isArray(data?.findings)) return data.findings as Finding[];
  if (Array.isArray(data?.results)) return data.results as Finding[];
  if (Array.isArray(data?.scanResults)) {
    return (data.scanResults as ScanResult[])
      .flatMap((r) => (Array.isArray(r.findings) ? r.findings : []))
      .filter(Boolean) as Finding[];
  }
  if (Array.isArray(data?.result?.results)) {
    return (data.result.results as ScanResult[])
      .flatMap((r) => (Array.isArray(r.findings) ? r.findings : []))
      .filter(Boolean) as Finding[];
  }
  return [];
};

export const extractScanResults = (payload: unknown): ScanResult[] => {
  const data = payload as { scanResults?: unknown; result?: { results?: unknown } };
  if (Array.isArray(data?.scanResults)) return data.scanResults as ScanResult[];
  if (Array.isArray(data?.result?.results)) return data.result.results as ScanResult[];
  return [];
};

export const extractSummary = (payload: unknown, findings: Finding[]): ReportSummary => {
  const data = payload as { summary?: unknown; report?: { summary?: unknown } };
  const summary = (data?.summary || data?.report?.summary) as Partial<ReportSummary> | undefined;
  if (summary && typeof summary === "object") {
    const totalValue = Number(summary.totalFindings);
    const criticalValue = Number(summary.critical);
    const highValue = Number(summary.high);
    const mediumValue = Number(summary.medium);
    const lowValue = Number(summary.low);
    const infoValue = Number(summary.info);
    return {
      totalFindings: Number.isFinite(totalValue) ? totalValue : findings.length,
      critical: Number.isFinite(criticalValue) ? criticalValue : 0,
      high: Number.isFinite(highValue) ? highValue : 0,
      medium: Number.isFinite(mediumValue) ? mediumValue : 0,
      low: Number.isFinite(lowValue) ? lowValue : 0,
      info: Number.isFinite(infoValue) ? infoValue : 0,
    };
  }
  return summaryFromFindings(findings);
};

export const parseScanStatus = (payload: unknown): LiveScanStatus => {
  const statusRaw = String(
    (payload as { status?: string; state?: string })?.status ||
      (payload as { state?: string })?.state ||
      (payload as { job?: { status?: string; state?: string } })?.job?.status ||
      (payload as { job?: { status?: string; state?: string } })?.job?.state ||
      "",
  ).toLowerCase();

  if (statusRaw.includes("error") || statusRaw.includes("fail")) {
    return "error";
  }
  if (
    statusRaw.includes("complete") ||
    statusRaw.includes("done") ||
    statusRaw.includes("success") ||
    statusRaw.includes("finished")
  ) {
    return "completed";
  }
  if (!statusRaw) return "running";
  return "running";
};

export const extractLiveLogText = (payload: unknown): string => {
  const lines = (payload as { liveLog?: { lines?: Array<{ line?: string }> } })?.liveLog?.lines;
  if (!Array.isArray(lines) || lines.length === 0) return "";
  return lines
    .map((entry) => String(entry?.line || "").trim())
    .filter(Boolean)
    .join("\n");
};
